import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { parseSearchParams } from '@/utils/stringUtils';
import { detokenize } from '@/utils/tokenizer';
import { logError } from '@/utils/logError';

interface UsePostHogDistinctIdResult {
  distinctId: string | undefined;
  loading: boolean;
}

interface UsePostHogDistinctIdOptions {
  /**
   * Set to false on a screen that redirects away before it could act on the
   * result — a prefilled access code, for instance, navigates on mount. The
   * screen it redirects to resolves the identity instead, so one activation
   * spends one decode rather than two.
   */
  enabled?: boolean;
}

/**
 * Hook to get PostHog distinct ID from URL params.
 * Supports both legacy ph_distinct_id and new ph_token.
 * ph_distinct_id takes precedence (backward compatibility).
 */
export const usePostHogDistinctId = (
  { enabled = true }: UsePostHogDistinctIdOptions = {},
): UsePostHogDistinctIdResult => {
  const location = useLocation();
  const searchParams = parseSearchParams(location.search);

  const urlDistinctId = enabled ? searchParams.ph_distinct_id?.trim() : undefined;
  const urlToken = enabled ? searchParams.ph_token?.trim() : undefined;

  const [distinctId, setDistinctId] = useState<string | undefined>(urlDistinctId);
  const [loading, setLoading] = useState<boolean>(!urlDistinctId && !!urlToken);

  useEffect(() => {
    // ph_distinct_id takes precedence (backward compatibility)
    if (urlDistinctId) {
      setDistinctId(urlDistinctId);
      setLoading(false);
      return undefined;
    }

    // If no token, nothing to do
    if (!urlToken) {
      return undefined;
    }

    // A screen can unmount before its decode resolves — a prefilled access code
    // redirects on mount, and the decode has up to DETOKENIZE_TIMEOUT_MS to run.
    // Drop the late result rather than setting state on an unmounted component.
    let cancelled = false;

    // Detokenize the ph_token
    const fetchDistinctId = async () => {
      setLoading(true);

      const result = await detokenize(urlToken);

      if (cancelled) {
        return;
      }

      if (result.success && result.value) {
        setDistinctId(result.value);
      } else {
        logError('[usePostHogDistinctId]', 'detokenization failed', result.error || 'Unknown error');
      }

      setLoading(false);
    };

    fetchDistinctId();

    return () => {
      cancelled = true;
    };
  }, [urlDistinctId, urlToken]);

  return { distinctId, loading };
};
